import { mapFileTypeToFileExtensions } from "@/components/dropdowns/fileTypeDropdown";
import {
  isoToDateOnly,
  toUtcStartOfDay,
  toUtcStartOfNextDay,
} from "@/utils/dateRanges";

type QueryParamTransform = (value: unknown) => [string, unknown] | null;

export const mapUrlQueryParamsToApiParams = (
  params: Record<string, unknown>,
  transforms: Record<string, QueryParamTransform> = {}
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(params)
      .map(([param, value]) => {
        const transform =
          transforms[param] ||
          ((nextValue) => [param, nextValue] as [string, unknown]);
        return transform(value);
      })
      .filter(Boolean) as Array<[string, unknown]>
  );

export const dataAssetQueryParamsToApiParamsTransforms: Record<
  string,
  QueryParamTransform
> = {
  fileTypes: (value) => {
    const selectedFileTypes = (Array.isArray(value) ? value : []) as Parameters<
      typeof mapFileTypeToFileExtensions
    >[0];
    const mappedValue = mapFileTypeToFileExtensions(selectedFileTypes)[1];
    return ["fileExtensions", mappedValue];
  },
  assetModifiedAfter: (value) => {
    const dateOnly = isoToDateOnly(value as string | null | undefined);
    const normalized = toUtcStartOfDay(dateOnly);
    return normalized ? ["assetModifiedAfter", normalized] : null;
  },
  assetModifiedBefore: (value) => {
    const dateOnly = isoToDateOnly(value as string | null | undefined);
    const normalized = toUtcStartOfNextDay(dateOnly);
    return normalized ? ["assetModifiedBefore", normalized] : null;
  },
};

export const mapDataAssetSearchParamsToApiParams = (
  params: Record<string, unknown>,
  transforms: Record<string, QueryParamTransform> = {}
) =>
  mapUrlQueryParamsToApiParams(params, {
    ...dataAssetQueryParamsToApiParamsTransforms,
    ...transforms,
  });

export const mapDataAssetStatisticsParamsToApiParams = (
  params: Record<string, unknown>
) =>
  mapDataAssetSearchParamsToApiParams(params, {
    page: () => null,
    size: () => null,
    sortField: () => null,
    sortOrder: () => null,
  });

const toSnakeCaseSearchBodyValue = (
  nextKey: string,
  value: unknown
): [string, unknown] | null => {
  if (value == null) return null;
  if (typeof value === "string" && value.length === 0) return null;
  if (Array.isArray(value) && value.length === 0) return null;
  return [nextKey, value];
};

export const mapDataAssetSearchParamsToRequestBody = (
  params: Record<string, unknown>
) =>
  mapUrlQueryParamsToApiParams(params, {
    query: (value) => toSnakeCaseSearchBodyValue("query", value),
    sealogEventQuery: (value) =>
      toSnakeCaseSearchBodyValue("sealog_event_query", value),
    exclude: (value) => toSnakeCaseSearchBodyValue("exclude", value),
    regex: (value) => toSnakeCaseSearchBodyValue("regex", value),
    fileTypes: (value) => {
      const selectedFileTypes = (Array.isArray(value) ? value : []) as Parameters<
        typeof mapFileTypeToFileExtensions
      >[0];
      const mappedValue = mapFileTypeToFileExtensions(selectedFileTypes)[1];
      return toSnakeCaseSearchBodyValue("file_extensions", mappedValue);
    },
    dataSetPublicIds: (value) =>
      toSnakeCaseSearchBodyValue("data_set_public_ids", value),
    manifestPublicIds: (value) =>
      toSnakeCaseSearchBodyValue("manifest_public_ids", value),
    realAssetPublicIds: (value) =>
      toSnakeCaseSearchBodyValue("real_asset_public_ids", value),
    realAssetDeploymentPublicIds: (value) =>
      toSnakeCaseSearchBodyValue("real_asset_deployment_public_ids", value),
    sealogEventValues: (value) =>
      toSnakeCaseSearchBodyValue("sealog_event_values", value),
    sealogEventFreeTexts: (value) =>
      toSnakeCaseSearchBodyValue("sealog_event_free_texts", value),
    sealogEventOptionNames: (value) =>
      toSnakeCaseSearchBodyValue("sealog_event_option_names", value),
    sealogEventOptionValues: (value) =>
      toSnakeCaseSearchBodyValue("sealog_event_option_values", value),
    assetModifiedAfter: (value) => {
      const dateOnly = isoToDateOnly(value as string | null | undefined);
      const normalized = toUtcStartOfDay(dateOnly);
      return normalized
        ? ["asset_modified_after", normalized]
        : null;
    },
    assetModifiedBefore: (value) => {
      const dateOnly = isoToDateOnly(value as string | null | undefined);
      const normalized = toUtcStartOfNextDay(dateOnly);
      return normalized
        ? ["asset_modified_before", normalized]
        : null;
    },
    sortField: (value) => toSnakeCaseSearchBodyValue("sort_field", value),
    sortOrder: (value) => toSnakeCaseSearchBodyValue("sort_order", value),
    page: () => null,
    size: () => null,
    verifiedOnly: () => null,
    cloudOnly: () => null,
  });
